import { User01 } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';

import Female from 'src/assets/female.svg?react';
import Male from 'src/assets/male.svg?react';
import { FirstAvailableModel } from 'src/services/availability';
import { BookingUserProfileModel } from 'src/services/booking';
import { isDoctorVirtualGp } from 'src/utils/doctor';
import { doctorAvatarSrc, doctorName } from 'src/utils/name';

type Props = {
    provider: BookingUserProfileModel;
    firstAvailable?: FirstAvailableModel;
    isLoadingFirstAvailable?: boolean;
    appointmentTypeDisplayOnly?: boolean;
};

export function ProviderOption({
    provider,
    firstAvailable,
    isLoadingFirstAvailable,
    appointmentTypeDisplayOnly,
}: Props) {
    const src = doctorAvatarSrc(provider);
    const isVirtual = isDoctorVirtualGp(provider);
    const img = src ? (
        <div className="relative size-8 flex-none self-start">
            <img className="block size-full rounded-full object-cover ring-1 ring-gray-200" src={src} />
            {provider.gender && (
                <i
                    className={classNames(
                        'text-3xs peer-checked:group-[]:ring-primary-25 absolute -bottom-1 -right-1 flex size-3.5 items-center justify-center rounded-full text-center ring-2 ring-white transition-colors',
                        {
                            'bg-blue-50 text-blue-400': provider.gender === 'M',
                            'bg-pink-50 text-pink-600': provider.gender === 'F',
                        }
                    )}
                >
                    {provider.gender === 'F' && <Female className="size-full" />}
                    {provider.gender === 'M' && <Male className="size-full" />}
                </i>
            )}
        </div>
    ) : (
        <div className="size-8 flex-none self-start rounded-full bg-gray-100 p-1.5 ring-1 ring-gray-200">
            <User01 className="block size-full text-gray-400" />
        </div>
    );
    return (
        <div className="flex h-full flex-col justify-start gap-1 md:gap-2" title={doctorName(provider)}>
            <div className="flex flex-row items-center gap-2">
                {img}
                <div className="flex flex-col">
                    <p
                        className={classNames('text-sm font-semibold leading-tight text-gray-900', {
                            'line-clamp-1': !!provider.umbracoRole,
                            'line-clamp-2': !provider.umbracoRole,
                        })}
                    >
                        {doctorName(provider)}
                    </p>
                    {provider.umbracoRole && <p className="text-xs text-gray-600">{provider.umbracoRole}</p>}
                </div>
            </div>
            {!isVirtual &&
                (isLoadingFirstAvailable ? (
                    <p className="w-full text-xs font-normal text-gray-300">
                        <span className="inline-block h-[0.5em] w-3/4 animate-pulse rounded-full bg-current opacity-25"></span>
                    </p>
                ) : firstAvailable?.firstAvailable ? (
                    <p className="animate-fade-in line-clamp-2 text-xs font-normal text-gray-600">
                        Next available:{' '}
                        <strong className="font-medium">
                            {moment(
                                `${firstAvailable.firstAvailable.date} ${firstAvailable.firstAvailable.time}`
                            ).calendar(null, {
                                nextWeek: 'ddd, D MMM [at] LT',
                                sameElse: 'ddd, D MMM [at] LT',
                            })}
                        </strong>
                    </p>
                ) : appointmentTypeDisplayOnly || provider.displayProfileNoOnlineBooking ? null : (
                    <p className="animate-fade-in text-xs font-normal text-gray-600">Not available</p>
                ))}
            {provider.umbracoProfileUrl && (
                <a
                    href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}${provider.umbracoPageUrl}`}
                    target="_blank"
                    className="text-primary-700 w-fit text-xs font-semibold hover:underline"
                >
                    View profile
                </a>
            )}
        </div>
    );
}
